import { useCallback, useState } from 'react';

import { copy } from '@perpay-web/fintech/utils/clipboardUtils';
import { useTimedEvent } from '@perpay-web/hooks/useTimedEvent';

const DEFAULT_MESSAGE = 'Copy';
const SUCCESS_MESSAGE = 'Copied';
const ERROR_MESSAGE = 'Error';

const DEFAULT_STATE = 'default';
const SUCCESS_STATE = 'success';
const ERROR_STATE = 'error';

const SHOW_DEFAULT_MESSAGE_DELAY_MS = 1500;

const getMessage = (copyState) => {
    if (copyState === ERROR_STATE) {
        return ERROR_MESSAGE;
    }
    if (copyState === SUCCESS_STATE) {
        return SUCCESS_MESSAGE;
    }
    return DEFAULT_MESSAGE;
};

const defaultChildGetter = (node) => node;

export const useClipboard = () => {
    const [copyState, setCopyState] = useState(DEFAULT_STATE);

    const timedEventCallback = useCallback(
        () => setCopyState(DEFAULT_STATE),
        [],
    );

    const { startTimer } = useTimedEvent(
        timedEventCallback,
        SHOW_DEFAULT_MESSAGE_DELAY_MS,
    );

    const onCopySuccess = () => {
        startTimer();
        setCopyState(SUCCESS_STATE);
    };

    const onCopyFail = () => {
        startTimer();
        setCopyState(ERROR_STATE);
    };

    const setClipboard = (
        elementRef,
        valueGetter,
        childGetter = defaultChildGetter,
    ) => {
        if (!elementRef.current) {
            onCopyFail();
            return Promise.reject(
                new Error('Copy failed due to missing element'),
            );
        }

        return copy(childGetter(elementRef.current), valueGetter).then(
            onCopySuccess,
            (e) => {
                onCopyFail();

                // Allow the caller to handle the failure case
                return Promise.reject(e);
            },
        );
    };

    return {
        message: getMessage(copyState),
        state: copyState,
        setClipboard,
    };
};
